// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = cartId ? getPromotionsDataView(state, cartId).value : getCurrentPromotionsDataView(state).value;
    const { isUpdatingCart } = state.pages.checkoutShipping;
    return {
        cart: cartState.value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading || isUpdatingCart,
        customerType: getSession(state).properties?.["mpExistingCustomer"],
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface CheckoutShippingCartTotalStyles {
    container?: GridContainerProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    buttonsGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
}

export const cartTotalStyles: CheckoutShippingCartTotalStyles = {
    container: { gap: 10 },
    cartTotalGridItem: { width: 12 },
    buttonsGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    continueButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                ])}
        `,
    },
};

const styles = cartTotalStyles;

const CheckoutShippingCartTotal: FC<Props> = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    customerType,
}) => {
    // <XNG-Changes>
    React.useEffect(() => {
        (window as any)._GaCheckoutFunnel && (window as any)._GaCheckoutFunnel(cart, 1);
        (window as any)._MpCheckoutFunnel && (window as any)._MpCheckoutFunnel(1, customerType);
    }, []);
    // </XNG-Changes>

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.cartTotalGridItem}>
                <CartTotalDisplay
                    extendedStyles={styles.cartTotal}
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                    // XNG-Changes
                    {...({ showCartUrl: true } as any)}
                />
            </GridItem>
            <GridItem {...styles.buttonsGridItem}>
                <Button
                    {...styles.continueButton}
                    type="submit"
                    disabled={isLoading}
                    data-test-selector="checkoutShippingCartTotalContinueButton"
                >
                    {/* XNG-Changes */}
                    {/* {translate("Continue")} */}
                    {translate("Next")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutShippingCartTotal),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
        displayName: "Cart Total",
    },
};

export default widgetModule;
